import { useEffect, useState } from "react";
import { ArrowLeft, DollarSign, Layers, Package, Palette, Wrench } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useSetPageHeader } from "@/lib/page-header";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media-query";
import { usePermissao } from "@/lib/use-permissao";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { ServiceCategorySection } from "@/components/catalogo/ServiceCategorySection";
import { CardapioSection } from "@/components/catalogo/CardapioSection";
import { EquipamentoSection } from "@/components/catalogo/EquipamentoSection";
import { ShadeOptionSection } from "@/components/catalogo/ShadeOptionSection";
import { PriceListSection } from "@/components/catalogo/PriceListSection";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { getCatalogoGerenciarTourSteps } from "@/lib/product-tour/page-tour-steps";

type SectionKey = "servicos" | "materiais" | "equipamentos" | "cores" | "precos";

type Section = {
  key: SectionKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
};

const SECTIONS: Section[] = [
  { key: "servicos", label: "Serviços", icon: Package, accent: "#0ea5e9" },
  { key: "materiais", label: "Materiais", icon: Layers, accent: "#d946ef" },
  // Rótulo curto: com cinco abas por extenso, a barra do celular estoura em 360px.
  { key: "equipamentos", label: "Equipam.", icon: Wrench, accent: "#f59e0b" },
  { key: "cores", label: "Cores", icon: Palette, accent: "#2457ff" },
  { key: "precos", label: "Preços", icon: DollarSign, accent: "#10b981" },
];

/** Deep-link do CatalogoHub (`#servicos`/`#materiais`/`#cores`/`#precos`) — antes era scroll-to-anchor
 *  puro (as 3 seções ficavam todas montadas ao mesmo tempo); agora só uma seção fica montada por
 *  vez, então o hash vira a aba ativa. */
function sectionFromHash(hash: string): SectionKey {
  const chave = hash.replace("#", "");
  return SECTIONS.some((s) => s.key === chave) ? (chave as SectionKey) : "servicos";
}

export function LabCatalogo() {
  const isMobile = useIsMobile();
  const { hash } = useLocation();
  const [section, setSection] = useState<SectionKey>(() => sectionFromHash(window.location.hash));

  /**
   * Trocar só o hash não remonta o componente, então ler no primeiro render não bastava: um link
   * salvo em `#cores`, aberto numa aba que já estava em `#materiais`, caía na aba errada — e o
   * mesmo valia pro voltar/avançar do navegador.
   *
   * Clicar numa aba não mexe no hash, então este efeito não interfere na navegação por clique.
   */
  useEffect(() => {
    if (hash) setSection(sectionFromHash(hash));
  }, [hash]);
  const meQuery = trpc.me.current.useQuery();
  const canManage = !!meQuery.data?.activeOrganization?.permissions?.["labCatalog.manage"];
  const podeVerPrecos = usePermissao("prices.view");
  // Quem só vê preço ("Ver preços" sem "Gerenciar catálogo") entra direto na seção Preços.
  const secoes = canManage
    ? SECTIONS.filter((s) => s.key !== "precos" || podeVerPrecos)
    : SECTIONS.filter((s) => s.key === "precos" && podeVerPrecos);
  const active = secoes.find((s) => s.key === section) ?? secoes[0];

  useTourDaPagina(getCatalogoGerenciarTourSteps(section));
  useSetPageHeader(
    {
      title: "Catálogo",
      subtitle:
        "Serviços, materiais, cores e preços do seu laboratório — é o que aparece em todo pedido feito com vocês",
    },
    [],
  );

  if (meQuery.data && !active) {
    return <NoAccessBlock description="O catálogo do laboratório é restrito a quem tem permissão de gerenciá-lo." />;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4">
      {/* Mesmo "voltar" de Configurações · Importar: texto discreto acima do conteúdo. */}
      {canManage && (
        <Link
          to="/lab/catalogo"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Catálogo
        </Link>
      )}

      {isMobile && <MobileTabs secoes={secoes} section={active.key} onChange={setSection} />}

      <div className="flex items-start gap-4">
        {!isMobile && <DesktopSidebar secoes={secoes} section={active.key} onChange={setSection} />}

        <div className="min-w-0 flex-1">
          {active.key === "servicos" ? (
            <ServiceCategorySection />
          ) : active.key === "materiais" ? (
            <CardapioSection />
          ) : active.key === "equipamentos" ? (
            <EquipamentoSection />
          ) : active.key === "cores" ? (
            <ShadeOptionSection />
          ) : (
            <PriceListSection />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Navegação por seção — uma seção montada por vez (mesmo padrão de SettingsClinic.tsx) ─

function DesktopSidebar({
  secoes,
  section,
  onChange,
}: {
  secoes: Section[];
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <nav data-tour="catalogo-gerenciar-secoes" className="sticky top-20 flex w-48 shrink-0 flex-col gap-1">
      {secoes.map((s) => {
        const active = section === s.key;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            className={cn(
              "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors",
              active
                ? "bg-zinc-50 text-zinc-900 dark:bg-muted/40 dark:text-foreground"
                : "text-zinc-600 hover:bg-zinc-50 dark:text-foreground/70 dark:hover:bg-muted",
            )}
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: `${s.accent}1a`, color: s.accent }}
            >
              <s.icon className="h-3.5 w-3.5" />
            </span>
            {s.label}
          </button>
        );
      })}
    </nav>
  );
}

function MobileTabs({
  secoes,
  section,
  onChange,
}: {
  secoes: Section[];
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <div
      data-tour="catalogo-gerenciar-secoes"
      className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white scrollbar-hide sm:-mx-6 dark:border-border dark:bg-card"
    >
      {/* Em 360px as quatro abas estouravam por 5px e viravam rolagem lateral. `justify-between`
          com padding menor distribui a sobra e elas cabem por extenso, sem rolar. */}
      <div className="flex justify-between gap-0.5 px-2 sm:justify-start sm:gap-1 sm:px-6">
        {secoes.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 border-b-2 px-2 py-2.5 text-xs font-medium transition-colors sm:px-3",
                active
                  ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-foreground/70 dark:hover:text-foreground",
              )}
            >
              <s.icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
